import { WOODLAND_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/woodlandBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { breathingGlowBlobs, gustingWeather, multiPlaneParallax, biomeCloser } from '../presetBuilders';

/** Jungle — Verdant Canopy: a lush, dense rainforest. Deep saturated tropical greens, heavy
 *  DAPPLED god-rays stabbing through the canopy, a humid green haze, drifting leaf/pollen motes,
 *  a living green light bed, falling leaves on a soft wind, dappled floor flicker, a layered
 *  deep-green canopy backdrop, and a saturated green grade. DARKER, DENSER + more dramatic than the
 *  cozy forest's gentle amber/mint — this is alive, humid, and vivid. */
export const JUNGLE: AtmospherePreset = {
  id: 'jungle',
  layers: [
    // Humid canopy sky — deep shadow-green zenith → filtered green horizon (kept rich, not pale,
    // so the dense canopy silhouettes read against it).
    { kind: 'skyGradient', from: 0x0c2415, to: 0x356b34 },
    // Leaf + pollen motes adrift — vivid greens and warm pollen gold, alive.
    {
      kind: 'fireflies',
      count: 28, colors: [P.leaf, 0xb6e85a, P.firefly],
      sizeMin: 4, sizeMax: 13, driftPx: 38,
      periodMinMs: 5000, periodMaxMs: 12000, alphaMin: 0.2, alphaMax: 0.7,
    },
    // Heavy DAPPLED god-rays stabbing through the canopy — many, dramatic, warm green-gold.
    {
      kind: 'godRays',
      count: 6, color: 0xdff0a8, widthMin: 40, widthMax: 120, height: 560,
      tiltDeg: 12, periodMinMs: 6000, periodMaxMs: 12000, alphaMin: 0.14, alphaMax: 0.46,
    },
    // Canopy light bloom (top) + deep forest-floor green glow (bottom), humidity breathing.
    breathingGlowBlobs(
      [
        { color: 0xdff0a8, atX: 0.5, atY: 0.1, size: 460, alpha: 0.1 },  // sun through canopy
        { color: 0x1f5a2a, atX: 0.5, atY: 0.97, size: 600, alpha: 0.14 }, // floor green glow
      ],
      { periodMs: 5600, min: 0.75, max: 1.3 },
    ),
    // Living green light bed — humid saturated greens drifting.
    {
      kind: 'lightField',
      count: 14, palette: [P.leaf, 0x2f7a35, 0x5aa84a],
      sizeMin: 110, sizeMax: 280, alphaMin: 0.05, alphaMax: 0.18,
      driftRadius: 70, periodMinMs: 6000, periodMaxMs: 12000,
    },
    // Falling leaves + pollen drifting down on a soft wind, density swelling on a slow cycle.
    gustingWeather(
      { mode: 'fall', color: 0x9cd86a, colorEnd: 0x4a8c3a,
        size: 10, frequencyMs: 120, lifespanMs: 4400, speed: 22, gravityY: 10, windX: 30, alphaEnd: 0.55 },
      { periodMs: 7200, min: 0.55, max: 1.5, phase: 0.25 },
    ),
    // Dappled sunlight pooling + shimmering on the jungle floor.
    {
      kind: 'flicker', color: 0xc8e890, size: 290, baseAlpha: 0.07, flickerAmp: 0.05, hzMin: 2, hzMax: 6,
      anchors: [{ atX: 0.22, atY: 0.92 }, { atX: 0.52, atY: 0.95 }, { atX: 0.8, atY: 0.92 }],
    },
    // Layered canopy depth (PX) read against the green sky — far peaks → mid ridge → near peaks, all
    // DEEP saturated green. Far is tallest + sways (foliage stirring); near is shortest + most opaque.
    ...multiPlaneParallax([
      { svg: WOODLAND_BACKDROPS.jungle.far, shape: 'peaks', color: 0x1b4226, speed: 0.04, height: 440, alpha: 0.74,
        detail: 0.9, sway: { ampPx: 16, periodMs: 24000 } },
      { svg: WOODLAND_BACKDROPS.jungle.mid, shape: 'ridge', color: 0x123018, speed: 0.10, height: 310, alpha: 0.86,
        detail: 1.2 },
      { svg: WOODLAND_BACKDROPS.jungle.near, shape: 'peaks', color: 0x0a1f10, speed: 0.2, height: 190, alpha: 0.95,
        detail: 1.1 },
    ]),
    // Humid green haze drifting through the understory.
    { kind: 'fog', color: 0x4a8c4a, bands: 5, alphaMin: 0.04, alphaMax: 0.12, driftPx: 50, periodMinMs: 9000, periodMaxMs: 16000 },
    ...biomeCloser({
      vignette: { color: 0x081a0e, edgeAlpha: 0.34, washColor: 0x3acb5a, washAlpha: 0.05 },
      // Saturated green grade — a 'normal' veil keeps it DEEP + lush (no 'screen' lift that would wash
      // the dense canopy pale).
      grade: { top: 0x6fcf5e, bottom: 0x123a1e, strength: 0.1, blend: 'normal' },
    }),
  ],
};
